import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAPI, postAPI } from '../../../utils/utils';
import { MODELS_EVENT_ID } from './modelFolders';

export default function useModelFolders({ enabled, socket, userInfo }) {
    const [data, setData] = useState(null);
    const [folderId, setFolderId] = useState(() => new URLSearchParams(window.location.search).get('carpeta') || null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const pending = useRef(false);
    const mounted = useRef(true);
    const requestId = useRef(0);
    const navigate = useCallback((id, { replace = false } = {}) => {
        const url = new URL(window.location.href);
        if (id) url.searchParams.set('carpeta', id);
        else url.searchParams.delete('carpeta');
        window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
        setFolderId(id);
    }, []);
    const refresh = useCallback(() => {
        if (!enabled || !userInfo?.id) return;
        setError('');
        const id = ++requestId.current;
        fetchAPI(`/model-folders?userId=${encodeURIComponent(userInfo.id)}`, result => {
            if (!mounted.current || id !== requestId.current) return;
            if (!result.success) { setError(result.error || 'No s’han pogut carregar les carpetes.'); return; }
            setData(result);
        }, false, () => {
            if (mounted.current && id === requestId.current) setError('No s’han pogut carregar les carpetes. Torna-ho a provar.');
        });
    }, [enabled, userInfo?.id]);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        if (!enabled) return;
        refresh();
        const changed = event => { if (String(event) === String(MODELS_EVENT_ID)) refresh(); };
        const back = () => setFolderId(new URLSearchParams(window.location.search).get('carpeta') || null);
        socket.on('.model_folders_updated', changed);
        socket.on('connect', refresh);
        window.addEventListener('popstate', back);
        return () => {
            socket.off('.model_folders_updated', changed);
            socket.off('connect', refresh);
            window.removeEventListener('popstate', back);
        };
    }, [enabled, socket, refresh]);
    useEffect(() => {
        if (data && folderId && !data.folders.some(f => f.id === folderId)) navigate(null, { replace: true });
    }, [data, folderId, navigate]);
    const mutate = useCallback((action, payload = {}) => new Promise((resolve, reject) => {
        if (pending.current) return reject(new Error('Hi ha una operació en curs.'));
        pending.current = true;
        setBusy(true);
        setError('');
        const finish = () => { pending.current = false; if (mounted.current) setBusy(false); };
        const failed = err => {
            finish();
            refresh();
            const message = err.error || err.message || 'No s’ha pogut completar l’operació.';
            if (mounted.current) setError(message);
            reject(new Error(message));
        };
        postAPI(`/model-folders/${action}`, { revision: data?.revision, ...payload, userId: userInfo?.id }, result => {
            if (!result.success) return failed(result);
            finish();
            refresh();
            resolve(result);
        }, failed);
    }), [data?.revision, refresh, userInfo?.id]);
    return enabled ? { data, folderId, navigate, refresh, mutate, busy, error } : null;
}
